<script lang="ts">
    // https://github.com/chanced/filedrop-svelte
    import type { Files } from 'filedrop-svelte';
    import FileDrop from 'filedrop-svelte';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';

    import InlineInfoAboutAccountDisabilities from '../../shared/InlineInfoAboutAccountDisabilities.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import ModalError from '../../shared/Modals/ModalError.svelte';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import { Navigation } from '../../utilities/Navigation';
    import {
        currentImport,
        type ImportFileTypeType,
        ImportResponseSchema,
    } from '../../shared/ColumnSelectPro/currentImport';
    import Select from '../../shared/Select.svelte';
    import { api } from '../../../stores/api';
    import { openModal } from 'svelte-modals';
    import { app } from '../../../stores/app';
    import { ImportApi } from './ImportApi';
    import { mainMenu } from '../../../stores/store';
    import SettingsRow from '../Settings/SettingsRow.svelte';
    import ColorSymbol from '../../shared/ColorSymbol.svelte';
    import SettingsRowLabel from '../Settings/SettingsRowLabel.svelte';
    import SettingsRowValue from '../Settings/SettingsRowValue.svelte';
    import TemplateSettingsGeneral from '../Settings/TemplateSettingsGeneral.svelte';
    import { importFileTypeItems, type ImportMethodType, importMethodTypeItems } from './Import';
    import { onMount } from 'svelte';
    import { AdminApi } from '../Accounts/AdminApi';
    import { SettingsApi } from '../Settings/SettingsApi';
    import { Umami } from '../../utilities/Umami';

    let file_type: ImportFileTypeType | '' = '';
    let import_type: ImportMethodType = 'upsert';

    let files: Files;
    /** Klucz i18n komunikatu dla użytkownika. `MessageDanger` tłumaczy `message` sam. */
    let errorMessage: string = '';
    /** Surowe szczegóły techniczne — `MessageDanger` ich nie tłumaczy, pokazuje tylko w trybie debug. */
    let error: string = '';
    let uploadingInProgress: boolean = false;
    let value: string = 'file.txt';
    const accept: string = '.xlsx, .xls, .csv, .ods'; // https://stackoverflow.com/questions/11832930/html-input-file-accept-attribute-file-type-csv#11834872

    let allow_file_import: boolean | null = null;

    switch ($mainMenu.currentPage) {
        case 'PageJobs':
            file_type = 'locations';
            break;
        case 'PageConnection':
            file_type = 'connections';
            break;
        default:
            break;
    }

    function upload() {
        errorMessage = '';
        error = '';
        uploadingInProgress = true;
        let importName = 'import_' + new Date().toISOString().slice(0, 10);
        if (file_type === '') {
            openModal(ModalError, {
                header: 'Error',
                message: 'No import data type selected.',
            });
            uploadingInProgress = false;
            return;
        }
        ImportApi.create(get(api), { name: importName, file_type, import_type })
            .then(response => {
                console.log('createImport response', response);
                ImportResponseSchema.parse(response);
                $currentImport = response;

                if ($currentImport !== null) {
                    get(api)
                        .sendFiles<unknown>(
                            files.accepted,
                            value,
                            `/import/${$currentImport.id}/upload`
                        )
                        .then(response => {
                            console.log('upload response', response);
                            Umami.event('import_file_upload', { import_type, file_type });
                            Navigation.navigate('/assign-columns');
                        })
                        .catch(response => {
                            console.error('error response', response);
                            switch (response.code) {
                                case 'import-upload-too-many-lines':
                                case 'max_locations_exceeded':
                                    let currentLines = response.variables[0].current ?? null;
                                    let maxLines = response.variables[0].max ?? null;
                                    if (currentLines === null || maxLines === null) {
                                        openModal(ModalError, {
                                            header: 'Error',
                                            message: response.code,
                                        });
                                        Umami.event(response.code, { import_type, file_type, currentLines: null, maxLines: null });
                                    } else {
                                        openModal(ModalError, {
                                            header: 'Error',
                                            message: response.code + '_ext',
                                            values: { currentLines, maxLines },
                                        });
                                        Umami.event(response.code, { import_type, file_type, currentLines, maxLines });
                                    }
                                    break;
                                default:
                                    openModal(ModalError, {
                                        header: 'Error',
                                        message: 'Failed to upload file.',
                                    });
                                    Umami.error('import_file_upload', response, { import_type, file_type });
                                    break;
                            }
                            if ($currentImport !== null) {
                                $currentImport.columns_assignment = null;
                            }
                        })
                        .finally(() => {
                            uploadingInProgress = false;
                        });
                }
            })
            .catch(err => {
                errorMessage = 'Failed to create import.';
                error = String(err);
                Umami.error('import_file_upload', err, { import_type, file_type });
                uploadingInProgress = false;
            });
    }

    onMount(() => {
        SettingsApi.getSettingsReadonly(get(api))
            .then(response => {
                allow_file_import = response.allow_file_import ?? true;
            })
            .catch(err => {
                console.error('Error while checking if import is disabled', err);
            });
    });

    $: {
        if (files && files.accepted) {
            upload();
        }
    }
</script>

{#if $app.account && $app.account?.isExpired() === true}
    <InlineInfoAboutAccountDisabilities text={$_('this_account_is_expired')} />
{:else if allow_file_import === false}
    <InlineInfoAboutAccountDisabilities text={$_('this_account_has_disabled_import')} />
{:else}
    <div style="padding: 10px 20px">
        <TemplateSettingsGeneral>
            <div slot="label">{$_('Import from file')}</div>
            <div slot="content">
                <SettingsRow>
                    <SettingsRowLabel>
                        {$_('Type of imported data')}
                    </SettingsRowLabel>
                    <SettingsRowValue>
                        <Select
                            useDefaultStyles={true}
                            bind:selected={file_type}
                            items={importFileTypeItems}
                            onChange={selected => {
                                console.log('Select', selected);
                                // @ts-ignore
                                file_type = selected;
                                return true;
                            }}
                        />
                    </SettingsRowValue>
                </SettingsRow>
                <SettingsRow>
                    <SettingsRowLabel>
                        {$_('Import type')}
                    </SettingsRowLabel>
                    <SettingsRowValue>
                        <Select
                            selected="upsert"
                            items={importMethodTypeItems}
                            onChange={s => {
                                console.log('Select', s, 'changed');
                                // @ts-ignore
                                import_type = s;
                                return true;
                            }}
                        />
                    </SettingsRowValue>
                </SettingsRow>
            </div>
        </TemplateSettingsGeneral>
    </div>

    {#if file_type !== ''}
        {#if uploadingInProgress || allow_file_import === null}
            <Spinner />
        {:else}
            {#if errorMessage}
                <MessageDanger header="Error" message={errorMessage} {error} />
            {/if}
            <div class="filedrop-container">
                <FileDrop
                    {accept}
                    on:filedrop={e => {
                        files = e.detail.files;
                    }}
                >
                    <div class="upload-container">
                        <div class="upload-inner">
                            <div class="upload-icon"></div>
                            <div class="upload-text">{$_('Drag and drop files here')}</div>
                            <div class="upload-link">{$_('Browse your device')}</div>
                        </div>
                    </div>
                </FileDrop>
                <div class="upload-info">
                    <div class="upload-text">{$_('Available formats')}: {accept}</div>
                    <!--                    <a-->
                    <!--                        href="./examples/AutoMapa_LastMile__example_input_file.csv"-->
                    <!--                        download-->
                    <!--                        class="upload-link">{$_('Get sample file')}</a-->
                    <!--                    >-->
                </div>
            </div>
            <!--{/if}-->
        {/if}
    {/if}
{/if}

<!--{#if files}-->
<!--    <h3>Accepted files</h3>-->
<!--    <ul>-->
<!--        {#each files.accepted as file}-->
<!--            <li>{file.name} </li>-->
<!--        {/each}-->
<!--    </ul>-->
<!--    <h3>Rejected files</h3>-->
<!--    <ul>-->
<!--        {#each files.rejected as rejected}-->
<!--            <li>{rejected.file.name} - {rejected.error.message}</li>-->
<!--        {/each}-->
<!--    </ul>-->
<!--{/if}-->

<style>
    .filedrop-container {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        font-size: 14px;
    }

    :global(.filedrop-container label) {
        width: 70%;
    }

    .upload-container {
        margin-top: 50px;
        /*width: 100%;*/
        /*max-width: 500px;*/
        height: 300px;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        font-size: 14px;
        /*min-width: 430px;*/
        width: 100%;
    }

    .upload-inner {
        border: 2px dashed #1d5fc9;
        border-radius: 15px;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        row-gap: 10px;
        cursor: pointer;
    }

    .upload-inner:hover {
        border: 2px dashed #1b5abf;
        background-color: #469ef444;
        transition: 0.3s;
    }

    .upload-icon {
        width: 76px;
        height: 71px;
        background-image: url('../../shared/Icons/svg/upload_blue.svg');
        opacity: 0.6;
        color: #1d5fc9;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
    }

    .upload-link {
        color: #1d5fc9;
        text-decoration: none;
        cursor: pointer;
        text-align: center;
    }

    .upload-link:hover {
        text-decoration: underline;
    }

    .upload-info {
        padding: 20px;
        display: flex;
        justify-content: center;
        flex-direction: column;
        row-gap: 10px;
    }

    label.button-primary {
        display: inline-block;
        border: 1px solid #1d5fc9;
        border-radius: 5px;
        background-color: #1d5fc9;
        color: #ffffff;
        height: 27px;
        line-height: 24px;
        font-size: 14px;
        box-sizing: border-box;
        cursor: pointer;
        margin: 2px 6px;
        margin-right: 6px;
        margin-bottom: 2px;
        text-align: center;
    }
</style>
